import { createContext, FC, PropsWithChildren, useContext, useMemo } from 'react'

import {
  CollectionServerSideFeatureFlags,
  FEATURE_FLAG_TYPE,
  FILTER_TYPE,
  FilterType,
  UrlPlacement,
} from '@src/server-side/feature-flags/feature-flag.types'

import { ECollectionPageType } from './services/collectionService/CollectionService.types'

export interface CollectionFeatureFlags {
  featureFlagType: typeof FEATURE_FLAG_TYPE.COLLECTION
  subCollectionUrlPlacement: UrlPlacement
  collectionPageType: ECollectionPageType
  filterType: FilterType

  isPersonalized: boolean
  bestSellerExperimentBucket: string | null
  clpFilterExperimentBucketMobile: string | null
  clpFilterExperimentBucketDesktop: string | null
}

const CONTEXT_ERROR_MESSAGE = 'useCollectionFeature must be used within a CollectionFeatureProvider'

export const CollectionFeatureContext = createContext<CollectionFeatureFlags | null>(null)

export const useCollectionFeature = (): CollectionFeatureFlags => {
  const context = useContext(CollectionFeatureContext)

  if (!context) {
    throw new Error(CONTEXT_ERROR_MESSAGE)
  }

  return context
}

interface CollectionFeatureProviderProps {
  serverSideFeatureFlags: CollectionServerSideFeatureFlags | null | undefined
}

export const CollectionFeatureProvider: FC<PropsWithChildren<CollectionFeatureProviderProps>> = ({
  children,
  serverSideFeatureFlags,
}) => {
  const featureFlags = useMemo(
    (): CollectionFeatureFlags => ({
      featureFlagType: FEATURE_FLAG_TYPE.COLLECTION,
      subCollectionUrlPlacement: serverSideFeatureFlags?.subCollectionUrlPlacement ?? 'path',
      collectionPageType: serverSideFeatureFlags?.collectionPageType ?? ECollectionPageType.GROUPED,
      filterType: serverSideFeatureFlags?.filterType ?? FILTER_TYPE.DROPDOWN,
      isPersonalized: serverSideFeatureFlags?.isPersonalized ?? false,
      bestSellerExperimentBucket: serverSideFeatureFlags?.bestSellerExperimentBucket ?? null,
      clpFilterExperimentBucketMobile: serverSideFeatureFlags?.clpFilterExperimentBucketMobile ?? null,
      clpFilterExperimentBucketDesktop: serverSideFeatureFlags?.clpFilterExperimentBucketDesktop ?? null,
    }),
    [serverSideFeatureFlags],
  )

  return <CollectionFeatureContext.Provider value={featureFlags}>{children}</CollectionFeatureContext.Provider>
}
